<template>
  <AppDialog
    :show="show"
    class="auto-track-rule-dialog"
    title="Automatic timer policy"
    size="medium"
    append-to-body
    @close="closeDialog"
  >
    <div class="row">
      <div class="col-xs-12">
        <div class="dialog-warning blue">
          <i class="hi hi-info-filled hi-12" />
          <div v-if="corporateAppType">
            This policy only applies to the Silent app. Team members with company owned devices can use the
            Silent app to run in the background.
          </div>
          <div v-else>
            This policy only applies to the desktop app. You can restrict which apps your members may use in
            <a
              class="clickable"
              :href="allowedAppsSettingsLink"
            >
              Allowed apps setting
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="upsellDialog"
      class="row"
    >
      <div class="col-xs-12">
        <div class="upgrade-banner">
          <div class="hi hi-lock hi-16"></div>
          <h3>{{ upsellCta.title }}</h3>
          <div>
            The Silent app tracks time and activity in the background for employees using company-owned devices. You can access it by upgrading to the Enterprise plan or by purchasing it as an add-on on other plans, both available on the billing page.
          </div>
          <div class="cta-block">
            <a
              class="btn modal-launcher btn-primary btn-sm"
              :href="upsellCta.href"
            >
              {{ upsellCta.cta }}
            </a>
            <a
              class="clickable learn-more"
              :href="corporateAppInfoUrl"
            >
              Learn more
            </a>
          </div>
        </div>
      </div>
    </div>
    <ValidationObserver
      v-if="!upsellDialog"
      ref="dialogObserver"
    >
      <div class="row">
        <div class="col-xs-12">
          <ValidationProvider
            v-slot="{ errors }"
            mode="eager"
            rules="required|min:3|max:50"
            name="name"
            :model-value="autoTrackRule.name"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label
                for="name"
                class="control-label required"
              >
                Policy name
              </label>
              <abbr title="required"></abbr>
              <AppInput
                id="name"
                v-model="autoTrackRule.name"
                type="text"
                placeholder="Enter a name for the policy"
              ></AppInput>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12">
          <ValidationProvider
            v-slot="{ errors }"
            name="member_ids"
            :model-value="autoTrackRule.member_ids"
          >
            <div :class="{ 'has-error': errors[0] }">
              <AutoTrackRulesMemberSelect
                :value="autoTrackRule.member_ids"
                :organization-id="organizationId"
                :auto-track-rule-id="autoTrackRuleId"
                @input="onMembersSelect"
                @select-all="onSelectAllMembers"
              />
              <div
                v-if="errors && errors.length > 0"
                class="dialog-warning orange members-with-policy"
              >
                <i class="hi hi-info-filled hi-12"></i>
                <div>
                  <a
                    v-tooltip="membersWithAutoTrackPolicyTooltipOptions"
                    class="clickable"
                    href="#"
                    :data-original-title="multipleMemberTrackingPolicyMessage(errors)"
                  >
                    {{ errors.length }} selected member{{ errors.length > 1 ? "s" : "" }}
                  </a> already belong{{ errors.length > 1 ? "" : "s" }} to another active track rule for {{ errors[0]?.organization_name }}.
                </div>
              </div>
            </div>
          </ValidationProvider>

          <div class="row form-group boolean optional">
            <div class="col-xs-12 checkbox">
              <AppCheckbox
                v-model="autoTrackRule.auto_add_new_users"
                :disabled="disableAutoAddNewUsersCheckBox"
                class="form-group boolean optional align-items-start"
              >
                <div class="d-flex checkbox-with-subtext checkbox-with-subtext-and-margin">
                  <div class="d-flex flex-column font-weight-normal">
                    <div>Set as the default auto-add policy for your organization</div>
                    <div class="subtext">New members will be automatically added to this policy</div>
                  </div>
                  <i
                    v-if="corporateAppType"
                    v-tooltip="tooltipOptions"
                    :title="autoAddNewUsersTooltipContent"
                    class="hi hi-info-filled hi-12 hi-tooltip mt-5"
                  />
                </div>
              </AppCheckbox>
            </div>
          </div>
          <div
            v-if="showAutoAddWarning"
            class="dialog-warning danger d-flex align-items-center"
          >
            <i class="hi hi-warning text-danger mr-10" /> {{ onePolicyAutoAddWarning }}
          </div>
        </div>
      </div>
      <div class="row form-group">
        <div class="col-xs-10">
          <label class="control-label">Timer start/stop rules</label>
          <AppSelect
            v-model.number="autoTrackRule.activated_by"
            label="Start type"
            show-loading-state
            :options="autoTrackOptions"
            :select2-options="select2Options"
            @input="onActivatedByChange"
          />
        </div>
      </div>
      <div
        v-show="showStartTimerActivityOption"
        class="row form-group boolean optional"
      >
        <div class="col-xs-12 checkbox">
          <AppCheckbox
            v-model="autoTrackRule.wait_until_active"
            class="form-group boolean optional auto-add-new-members"
          >
            <span>Don't start timer until first activity</span>
            <i
              v-tooltip="tooltipOptions"
              :title="timerTooltip"
              class="hi hi-info-filled hi-12 hi-tooltip"
            />
          </AppCheckbox>
        </div>
      </div>
      <div
        v-show="numberOfMembersWithoutShifts && activatedByShifts"
        class="dialog-warning orange members-without-shifts"
      >
        <i class="hi hi-info-filled hi-12"></i>
        <div>
          <a
            v-tooltip="membersWithoutShiftsTooltipOptions"
            :data-original-title="membersWithoutShiftsTooltip"
            class="clickable"
            href="#"
          >
            {{ numberOfMembersWithoutShifts }} {{ $filters.pluralize(numberOfMembersWithoutShifts, 'member') }}
          </a>
          {{ numberOfMembersWithoutShifts == 1 ? 'does' : 'do' }} not have shifts. Auto start rules will apply once
          shifts are added.
        </div>
      </div>
      <div
        v-show="showRulesOptions"
        class="row rule-options"
      >
        <div class="col-xs-12 col-sm-6 form-group">
          <ValidationProvider
            v-slot="{ errors }"
            name="days_of_week"
            :model-value="autoTrackRule.rules.days_of_week"
          >
            <div :class="{ 'has-error': errors[0] }">
              <div>
                <label class="control-label">Days of the week</label>
              </div>
              <AppIntervalDayPicker
                id="weekdays"
                v-model="autoTrackRule.rules.days_of_week"
                mode="week"
                multiple
              ></AppIntervalDayPicker>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="col-xs-6 col-sm-3">
          <ValidationProvider
            v-slot="{ errors }"
            name="start_time"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label">Start</label>
              <i
                v-tooltip="tooltipOptions"
                :title="timeZoneTooltip"
                class="hi hi-info-filled hi-12 hi-tooltip"
              />
              <AppTimeInput
                v-model="autoTrackRule.rules.start_time"
                show-seconds="never"
              ></AppTimeInput>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="col-xs-6 col-sm-3">
          <ValidationProvider
            v-slot="{ errors }"
            name="stop_time"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label">Stop</label>
              <AppTimeInput
                v-model="autoTrackRule.rules.stop_time"
                show-seconds="never"
              ></AppTimeInput>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>
      <div class="row form-group">
        <div class="col-xs-12">
          <ValidationProvider
            v-slot="{ errors }"
            name="start_project_id"
            :model-value="autoTrackRule.start_project_id"
          >
            <div :class="{ 'has-error': errors[0] }">
              <div>
                <label class="control-label">Starting project</label>
                <i
                  v-tooltip="tooltipOptions"
                  :title="projectTooltip"
                  class="hi hi-info-filled hi-12 hi-tooltip"
                />
              </div>
              <AppSelect
                v-model="autoTrackRule.start_project_id"
                show-loading-state
                :options="projectsForSelect"
                :select2-options="projectSelect2Options"
                @input="onProjectSelect"
              ></AppSelect>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
          <div
            v-show="numberOfMembersNotInProject"
            id="members-to-add"
            class="dialog-warning orange"
          >
            <i class="hi hi-info-filled hi-12"></i>
            <div>
              <a
                v-tooltip="membersNotInProjectTooltipOptions"
                class="clickable"
                href="#"
                :data-original-title="membersNotInProjectTooltip"
              >
                {{ numberOfMembersNotInProject }} {{ $filters.pluralize(numberOfMembersNotInProject, 'member') }}
              </a>
              will be automatically added as a 'user' to the starting project.
            </div>
          </div>
          <ValidationProvider
            v-if="showConsent"
            v-slot="{ errors }"
            :rules="{ required: { allowFalse: false } }"
            name="corporate_notice_consent"
            :model-value="autoTrackRule.corporate_notice_consent"
          >
            <div :class="{ 'has-error': errors[0] }">
              <AppCheckbox v-model="autoTrackRule.corporate_notice_consent">
                <span>
                  I have read and understood the
                  <a @click.prevent="openConsentDialog">reminder to give notice</a>
                  to team members
                </span>
              </AppCheckbox>

              <div
                v-if="errors[0]"
                class="corporate-consent-warning"
              >
                <i class="hi hi-warning text-danger mr-10" /> You must acknowledge and agree to continue
              </div>
            </div>
          </ValidationProvider>
        </div>
      </div>
    </ValidationObserver>
    <template
      v-if="!upsellDialog"
      #footer
    >
      <AppButton
        class="cancel"
        @click="closeDialog"
      >
        Cancel
      </AppButton>
      <AppButton
        type="primary"
        :disabled="sendingRequest"
        @click="save"
      >
        Save
      </AppButton>
    </template>
  </AppDialog>
</template>

<script>
import AutoTrackRulesMemberSelect from './AutoTrackRulesMemberSelect.vue';
import {
  AutoTrackRulesResource,
  OrganizationsResource,
  ProjectsResource,
  AttendanceSchedulesResource,
} from '~/resources';
import { extend } from '~/utils/vee_validate_compat';
import { min, max } from '@vee-validate/rules';
import { cloneDeep, escape, xor } from 'lodash-es';
import { markRaw } from 'vue';

extend('min', {
  ...min,
  message: 'is too short (minimum is three characters)',
});

extend('max', {
  ...max,
  message: 'is too long (maximum is fifty characters)',
});

export default {
  name: 'AutoTrackRuleDialog',
  components: {
    AutoTrackRulesMemberSelect,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    projects: {
      type: Array,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    editing: {
      type: Boolean,
      required: true,
    },
    editingAutoTrackRule: {
      type: Object,
      default: () => ({}),
    },
    autoTrackOptions: {
      type: Array,
      default: () => [],
    },
    autoAddNewUsersRule: {
      type: Object,
      default: null,
    },
    appType: {
      type: String,
      default: '',
    },
    upsellDialog: {
      type: Boolean,
      required: true,
    },
    upsellCta: {
      type: Object,
      default: () => ({}),
    },
    corporateAppInfoUrl: {
      type: String,
      required: true,
    },
    defaultAutoAdd: {
      type: Boolean,
      default: false,
    },
    existingPolicy: {
      type: Object,
      default: () => ({ id: null, name: '' }),
    },
  },
  data() {
    return {
      select2Options: Object.freeze({
        allowClear: false,
        multiple: false,
        width: '70%',
      }),
      projectSelect2Options: Object.freeze({ width: '100%' }),
      defaultPolicy: {
        name: '',
        member_ids: [],
        wait_until_active: true,
        activated_by: 'fixed_schedule',
        start_project_id: 0,
        rules: {
          days_of_week: [],
          start_time: markRaw(moment(9, 'HH')),
          stop_time: markRaw(moment(17, 'HH')),
        },
        corporate_notice_consent: false,
        auto_add_new_users: false,
      },
      sendingRequest: false,
      timerTooltip:
        'Enabling this setting will make sure the timer does not start until the mouse or keyboard is active.',
      timeZoneTooltip: 'Time Zones are based on the members location when they track time.',
      tooltipOptions: Object.freeze({
        placement: 'top',
      }),
      membersNotInProjectTooltip: '',
      membersNotInProjectTooltipOptions: Object.freeze({
        placement: 'top',
        html: true,
      }),
      membersWithoutShiftsTooltip: '',
      membersWithoutShiftsTooltipOptions: Object.freeze({
        placement: 'top',
        html: true,
      }),
      membersWithAutoTrackPolicyTooltipOptions: Object.freeze({
        placement: 'top',
        html: true,
      }),
      showStartTimerActivityOption: !this.editing || this.editingAutoTrackRule.activated_by !== 'activity',
      showRulesOptions: !this.editing || this.editingAutoTrackRule.activated_by === 'fixed_schedule',
      originalMemberIds: this.editing ? this.editingAutoTrackRule.member_ids : [],
      autoTrackRuleId: this.editing ? this.editingAutoTrackRule.id : null,
      numberOfMembersNotInProject: 0,
      numberOfMembersWithoutShifts: 0,
      showConsentDialog: false,
      previousMemberIds: [],
      requestingMembersWithoutShifts: false,
      maxMembersCount: 6,
      // The full eligible set captured when the user clicks "Select all" in the member picker. Its
      // presence means select-all mode (see allMembersSelected); exclusions are derived from it as
      // the user deselects, and we submit select_all_members + excluded_user_ids instead of every
      // member id. Reset per open via dialogKey (the component is re-created).
      selectAllMemberIds: [],
    };
  },
  computed: {
    projectsForSelect() {
      const projectsCopy = cloneDeep(this.projects);

      if (this.corporateAppType) {
        projectsCopy.find((project) => project.id === 0).name =
          'Most recent tracked or default project if no project has tracked time';
      }

      return projectsCopy.map((project) => ({
        ...project,
        text: project.name,
      }));
    },
    allowedAppsSettingsLink() {
      return OrganizationsResource.allowedAppsSettingsLink({ id: this.organizationId });
    },
    autoTrackRule() {
      return this.editing ? this.editingAutoTrackRule : this.defaultPolicy;
    },
    disableAutoAddNewUsersCheckBox() {
      return this.corporateAppType && (this.defaultAutoAdd || this.isExistingAutoAdd);
    },
    autoAddNewUsersTooltipContent() {
      return `An organization using Silent app must have 1 policy that automatically adds new members.
      This policy can only be deselected by selecting another Silent app policy as the default auto-add.`;
    },
    corporateAppType() {
      return this.appType === 'corporate';
    },
    appTypeLocalization() {
      return this.corporateAppType ? 'Silent app' : this.appType
    },
    projectTooltip() {
      if (this.corporateAppType) {
        return `Select a project that the timer will start tracking time to automatically.
        If a project is not selected, time will track to the most recent tracked project or
        to Default Project if no project has tracked time.`;
      }

      return `Select the project that the timer will start tracking time to automatically.
      If the project is removed, the timer will start tracking to the most recent project for each individual member.`;
    },
    showConsent() {
      return this.corporateAppType && !this.editing;
    },
    requireCorporateConsent() {
      if (this.corporateAppType && !this.editing) {
        return !this.autoTrackRule.corporate_notice_consent;
      }

      return false;
    },
    activatedByShifts() {
      return this.autoTrackRule.activated_by === 'shifts';
    },
    memberIdsChanged() {
      // Order-insensitive; xor stays O(n) on the large arrays "Select all" produces.
      return xor(this.previousMemberIds, this.autoTrackRule.member_ids).length > 0;
    },
    allMembersSelected() {
      return this.selectAllMemberIds.length > 0;
    },
    // Members deselected after "Select all" - the current selection is always a subset of the
    // captured eligible set, so this is the complement the server excludes.
    excludedMemberIds() {
      if (!this.allMembersSelected) {
        return [];
      }

      // Both arrays hold the full org member list after "Select all"; a Set lookup keeps this
      // linear instead of freezing the tab on tens of thousands of members.
      const selectedIds = new Set(this.autoTrackRule.member_ids);

      return this.selectAllMemberIds.filter((id) => !selectedIds.has(id));
    },
    onePolicyAutoAddWarning() {
      return `Only 1 ${this.appTypeLocalization} policy per organization can automatically add new members.
      Selecting this policy as the new default will remove auto-add from ${this.existingPolicy.name}.`;
    },
    isExistingAutoAdd() {
      return this.existingPolicy.id === this.autoTrackRuleId;
    },
    showAutoAddWarning() {
      return (
        this.autoTrackRule.auto_add_new_users &&
        !this.defaultAutoAdd &&
        !this.isExistingAutoAdd &&
        this.existingPolicy.name
      );
    },
  },
  created() {
    this.defaultPolicy.auto_add_new_users = this.corporateAppType ? this.defaultAutoAdd : false;

    if (this.editing) {
      this.autoTrackRule.start_project_id = this.autoTrackRule.start_project_id || 0;
    }

    this.loadMembersWithoutShifts();
  },
  methods: {
    closeDialog() {
      this.$emit('close', this.originalMemberIds);
    },
    onActivatedByChange(value) {
      this.showRulesOptions = value === 'fixed_schedule';
      this.showStartTimerActivityOption = value !== 'activity';
      this.loadMembersWithoutShifts();
    },
    multipleMemberTrackingPolicyMessage(errors) {
      const names = errors.slice(0, this.maxMembersCount).map((error) => error.member_name);

      return this.memberPreviewTooltip(names, errors.length);
    },
    // Single builder for the member-preview tooltips: escaped names (user-controlled content in
    // HTML tooltips), one per line, with a "+N more" tail when the total exceeds the preview.
    memberPreviewTooltip(names, totalCount) {
      let tooltip = names.map((name) => escape(name)).join('<br>');
      const extraMembersCount = totalCount - names.length;

      if (extraMembersCount > 0) {
        tooltip = tooltip + `<br><b>+${extraMembersCount} more</b>`;
      }

      return tooltip;
    },
    onMembersSelect(memberIds) {
      this.autoTrackRule.member_ids = memberIds;

      if (memberIds.length === 0) {
        this.previousMemberIds = [];
        this.numberOfMembersWithoutShifts = 0;
        this.selectAllMemberIds = [];
      }

      this.membersNotInProject(memberIds, Number(this.autoTrackRule.start_project_id));
      this.loadMembersWithoutShifts();
    },
    onSelectAllMembers() {
      this.selectAllMemberIds = [...this.autoTrackRule.member_ids];
    },
    loadMembersWithoutShifts() {
      if (
        this.autoTrackRule.member_ids.length > 0 &&
        this.activatedByShifts &&
        this.memberIdsChanged &&
        this.requestingMembersWithoutShifts === false
      ) {
        this.requestingMembersWithoutShifts = true;
        const requestedIds = [...this.autoTrackRule.member_ids];
        const stillCurrent = () => xor(this.autoTrackRule.member_ids, requestedIds).length === 0;
        const params = { organizationId: this.organizationId };

        AttendanceSchedulesResource.membersWithoutShifts(params, { user_ids: requestedIds })
          .then((response) => {
            if (!stillCurrent()) {return;}

            this.previousMemberIds = requestedIds;

            this.numberOfMembersWithoutShifts = response.count || 0;
            this.membersWithoutShiftsTooltip = this.memberPreviewTooltip(response.names || [], response.count || 0);
          })
          .finally(() => {
            this.requestingMembersWithoutShifts = false;
            if (!stillCurrent()) {
              this.loadMembersWithoutShifts();
            }
          });
      }
    },
    onProjectSelect(projectId) {
      this.membersNotInProject([...this.autoTrackRule.member_ids], Number(projectId));
    },
    membersNotInProject(memberIds, projectId) {
      const validProjectId = projectId !== undefined && projectId !== null && projectId !== 0 && !isNaN(projectId);

      if (memberIds.length > 0 && validProjectId) {
        ProjectsResource.membersNotInProject({ id: projectId }, { member_ids: memberIds }).then((response) => {
          // The server returns a count plus a short name preview (same contract as
          // members_without_shifts) so large orgs do not ship a name per member.
          this.numberOfMembersNotInProject = response.count || 0;
          this.membersNotInProjectTooltip = this.memberPreviewTooltip(response.names || [], response.count || 0);
        });
      } else {
        this.numberOfMembersNotInProject = 0;
      }
    },
    save() {
      this.$refs.dialogObserver.validate().then((result) => {
        if (result) {
          this.sendingRequest = true;
          const params = this.editing
            ? { organizationId: this.organizationId, id: this.autoTrackRuleId }
            : { organizationId: this.organizationId };

          // eslint-disable-next-line vue/no-mutating-props
          delete this.editingAutoTrackRule.id;

          let start_project_id = this.autoTrackRule.start_project_id;

          if (Number(start_project_id) === 0) {
            start_project_id = null;
          }

          const payload = {
            ...this.autoTrackRule,
            start_project_id,
            auto_add_new_users: this.autoTrackRule.auto_add_new_users ?? false,
            rules: {
              ...this.autoTrackRule.rules,
              wait_until_active:
                this.autoTrackRule.rules.activated_by === 'activity'
                  ? true
                  : this.autoTrackRule.rules.wait_until_active,
              start_time: this.autoTrackRule.rules.start_time.format('HH:mm'),
              stop_time: this.autoTrackRule.rules.stop_time.format('HH:mm'),
            },
            app_type: this.appType,
          };

          // "Select all members" ships a flag plus the deselected ids instead of every member id;
          // the server expands it to the eligible set.
          if (this.allMembersSelected) {
            payload.select_all_members = true;
            payload.excluded_user_ids = this.excludedMemberIds;
            delete payload.member_ids;
          }

          const method = this.editing ? 'update' : 'create';

          AutoTrackRulesResource[method](params, { auto_track_rule: payload })
            .then((response) => {
              if (response.success) {
                this.$emit('rule-saved');
              } else {
                if (response.errors) {
                  this.$refs.dialogObserver.setErrors(response.errors);
                }
              }
              this.$appMessage(response.notice);
            })
            .catch((_error) => {
              this.$appMessage('Unexpected error. Please try again.');
            })
            .finally(() => {
              this.sendingRequest = false;
            });
        }
      });
    },
    openConsentDialog() {
      this.$emit('consent-link-clicked');
    },
  },
};
</script>
